<template>
  <div>
    <loader
      :is-loading="logsLoading && !hasNewLogs"
      data-cy="logs-loader"
      background-color="BACKGROUND_LOADER_COLOR"
    />
    <div class="relative flex h-full flex-col space-y-5 pr-1">
      <div
        v-if="logs.length"
        ref="infiniteScrollWrapper"
        :class="{
          'rounded-sm bg-white p-6': isCurrentTabEmail || isInboxPage,
        }"
        class="overflow-auto"
      >
        <div
          :class="{
            'divide-y rounded-sm border': isCurrentTabEmail || isInboxPage,
          }"
        >
          <div
            v-for="(log, index) in logs"
            :key="index"
            :data-cy="isInboxPage ? 'thread-logs-block' : 'logs-block'"
            :class="{ 'pb-5': !isCurrentTabEmail && !isInboxPage }"
          >
            <communication-thread
              v-if="isInboxPage"
              :key="`thread_${index}`"
              :log="log as CommunicationLogBase"
            />
            <component
              :is="getLogComponent(log)"
              v-else
              :log="log as TaskLog"
            />
          </div>
        </div>
        <div v-if="store.hasNoMoreData" class="mb-5 p-5 text-center">
          <div class="flex w-full items-center justify-center space-x-5">
            <hr class="w-2/5 border" />
            <p class="w-1/5 text-center text-xs font-bold">
              <span>
                {{ $t("COMMON.NO") }}
                {{ $t("COMMON.MORE") }}
                {{ $t("COMMON.DATA") }}
              </span>
            </p>
            <hr class="w-2/5 border" />
          </div>
        </div>
      </div>
      <div
        v-else-if="!logsLoading && !logs?.length"
        class="w-full justify-center text-center"
      >
        {{ $t("ACTIVITY.NO_ACTIVITIES_LOGGED") }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useInfiniteScroll } from "@vueuse/core";
import { useRouteQuery } from "@vueuse/router";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import {
  type LocationQuery,
  type LocationQueryValue,
  useRoute,
} from "vue-router";

import { deepEqual } from "fast-equals";
import cloneDeep from "lodash/cloneDeep";
import omitBy from "lodash/omitBy";
import { storeToRefs } from "pinia";

import { CommunicationType } from "@/enums/communicationLogs";
import { convertToArray, isNullish } from "@/helpers/common";
import {
  EMAIL_UPDATED,
  NOTE_UPDATED,
  SMS_UPDATED,
} from "@/helpers/constants/events";
import { useAuth } from "@/hooks/auth";
import { useEmitter, usePromiseWrapper } from "@/hooks/common";
import { useCommunicationLogs } from "@/hooks/communicationLogs";
import { useDeals } from "@/hooks/deals";
import router from "@/router";
import { ROUTE_DEALS, ROUTE_DEAL_ACTIVITY, ROUTE_INBOX } from "@/router/routes";
import { useCommunicationStore } from "@/stores/communication";

import CommunicationLogCall from "@/components/communicationLogs/communication/CommunicationLogCall.vue";
import CommunicationLogEmail from "@/components/communicationLogs/communication/CommunicationLogEmail.vue";
import CommunicationLogNote from "@/components/communicationLogs/communication/CommunicationLogNote.vue";
import CommunicationLogSms from "@/components/communicationLogs/communication/CommunicationLogSms.vue";
import CommunicationLogTask from "@/components/communicationLogs/communication/CommunicationLogTask.vue";
import CommunicationThread from "@/components/communicationLogs/communication/CommunicationThread.vue";
import CommunicationLogEmailThread from "@/components/communicationLogs/communication/emailThread/CommunicationLogEmailThread.vue";
import Loader from "@/components/ui/Loader.vue";

import type { CommunicationFilters, TaskLog } from "@/models/communicationLogs";
import type {
  CommunicationLogBase,
  EmailThread,
} from "@/models/communicationLogs";

const emit = defineEmits<{
  "email-tab:open": [];
}>();

const LOG_COMPONENTS = {
  [CommunicationType.task]: CommunicationLogTask,
  [CommunicationType.call]: CommunicationLogCall,
  [CommunicationType.email]: CommunicationLogEmail,
  [CommunicationType.sms]: CommunicationLogSms,
  [CommunicationType.note]: CommunicationLogNote,
} as const;

const PARAMS_TO_RESET_PAGE = [
  "task_statuses",
  "task_priority_level",
  "task_due_date",
  "application_ids",
  "assignee_ids",
  "call_session_caller_ids",
  "search",
  "sort_by",
  "call_session_created_at",
  "type",
  "activity_status",
  "email_is_outgoing",
  "recipient_ids",
  "sender_ids",
  "created_at",
  "email_is_automated",
  "email_statuses[]",
  "sms_statuses[]",
  "sms_types[]",
  "sms_created_at",
  "email_created_at",
];

const ALLOWED_QUERY_KEYS = {
  shared: ["application_ids", "sort_by", "search", "type", "page"],
  [CommunicationType.task]: [
    "task_due_date",
    "assignee_ids",
    "task_priority_level",
    "task_statuses",
  ],
  [CommunicationType.call]: [
    "call_session_caller_ids",
    "call_session_created_at",
  ],
  [CommunicationType.email]: [
    "email_statuses[]",
    "email_is_outgoing",
    "recipient_ids",
    "sender_ids",
    "email_created_at",
  ],
  [CommunicationType.sms]: [
    "sender_ids",
    "sms_created_at",
    "sms_statuses[]",
    "sms_types[]",
  ],
  [CommunicationType.note]: [],
};

const requestsMap = new Map();
const infiniteScrollWrapper = ref<HTMLDivElement | null>(null);

const store = useCommunicationStore();
const { activeDeal } = useDeals();
const { hasNewLogs } = storeToRefs(store);

const route = useRoute();
const auth = useAuth();
const { isInboxPage, isCurrentTabEmail } = useCommunicationLogs();
const emitter = useEmitter();

const logs = computed(() =>
  isCurrentTabEmail.value && !isInboxPage.value
    ? store.emailThreads
    : store.logs
);

const page = useRouteQuery("page");

const scrollTop = () => {
  infiniteScrollWrapper.value?.scrollTo({ top: 0, behavior: "smooth" });
};

const getLogComponent = (log: CommunicationLogBase | EmailThread) => {
  if ("type" in log) {
    return LOG_COMPONENTS[log.type];
  }

  return CommunicationLogEmailThread;
};

const refreshLogsHandler = async () => {
  await getCommunicationList({
    ...route.query,
    page: "1",
  } as Partial<CommunicationFilters>);
  hasNewLogs.value = false;
};

emitter.on(EMAIL_UPDATED, () =>
  isInboxPage.value ? getCommunicationList() : handleEmailTabLogs(false)
);

emitter.on(SMS_UPDATED, () => getCommunicationList());
emitter.on(NOTE_UPDATED, () =>
  getCommunicationList({ ...route.query, page: "1" })
);

const { fetchWrapper: getCommunicationList, loading: logsLoading } =
  usePromiseWrapper(
    async (filters: Partial<CommunicationFilters> = route.query) => {
      const query = cloneDeep(filters);

      if (query.type) {
        const allowedKeys = ALLOWED_QUERY_KEYS.shared.concat(
          ALLOWED_QUERY_KEYS[query.type as CommunicationType] ?? []
        );
        Object.keys(query).forEach((key) => {
          if (!allowedKeys.includes(key)) {
            delete query[key as keyof typeof query];
          }
        });
      }

      if (activeDeal.value.id && route.name !== ROUTE_INBOX) {
        query.application_ids = [activeDeal.value.id];
      }

      if (query.task_statuses?.length) {
        query.task_statuses = convertToArray(query.task_statuses);
      }

      if (query.sender_ids?.length) {
        query.sender_ids = convertToArray(query.sender_ids);
      }

      if (!logs.value.length) {
        query.page = "1";
      }

      if (!query.type && !auth.isLendflowUser) {
        query.email_is_automated = query.email_is_automated ?? "0";
      }

      const filledQuery = omitBy(query, (value, key) => {
        const valueCheck = value === "" || isNullish(value);
        const keyCheck = key === "email_is_automated";
        return query.type ? valueCheck || keyCheck : valueCheck;
      });

      const activeRequestKey = new URLSearchParams(
        filledQuery as Record<string, string>
      ).toString();

      if (requestsMap.get(activeRequestKey)) {
        return;
      }

      requestsMap.set(activeRequestKey, true);

      const request =
        isCurrentTabEmail.value && !isInboxPage.value
          ? store.getEmailThreads
          : store.getCommunicationList;

      await router.push({ query: filledQuery });
      await request(filledQuery, isInboxPage.value);
    },
    {
      finally: () => {
        requestsMap.clear();
      },
    }
  );

const loadMore = () => {
  const newPage = Number(page.value || 1) + 1;
  page.value = newPage.toString();
};

const handleTaskTabLogs = async (shouldStartFromFirstPage: boolean) => {
  const {
    type,
    sort_by,
    application_ids,
    search,
    task_due_date,
    assignee_ids,
    task_priority_level,
    task_statuses,
  } = route.query;

  const query: LocationQuery = {
    type,
    sort_by,
    application_ids,
    search,
    task_due_date,
    assignee_ids,
    task_priority_level,
    task_statuses,
    page: (shouldStartFromFirstPage ? "1" : page.value) as LocationQueryValue,
  };

  if (!route.query.assignee_ids && auth?.currentUser?.id) {
    query.assignee_ids = isInboxPage.value
      ? convertToArray(auth.currentUser.id.toString())
      : null;
  }

  await router.push({ query });

  getCommunicationList(query);
};

const handleCallTabLogs = async (shouldStartFromFirstPage: boolean) => {
  const {
    type,
    sort_by,
    application_ids,
    search,
    task_priority_level,
    call_session_caller_ids,
    task_statuses,
    created_at,
  } = route.query;

  const query: LocationQuery = {
    type,
    sort_by,
    application_ids,
    search,
    task_priority_level,
    call_session_caller_ids,
    task_statuses,
    created_at,
    page: (shouldStartFromFirstPage ? "1" : page.value) as LocationQueryValue,
  };

  if (!route.query.call_session_caller_ids) {
    query.call_session_caller_ids = isInboxPage.value
      ? convertToArray(auth.currentUser?.id.toString())
      : null;
  }

  await router.push({ query });

  getCommunicationList(query);
};

const handleEmailTabLogs = async (
  shouldStartFromFirstPage: boolean,
  shouldAddSender = false
) => {
  const {
    type,
    sort_by,
    application_ids,
    search,
    email_statuses,
    email_is_outgoing,
    recipient_ids,
    sender_ids,
    created_at,
  } = route.query;

  const query: LocationQuery = {
    type,
    sort_by,
    page: page.value as LocationQueryValue,
    search,
    email_statuses,
    email_is_outgoing,
    recipient_ids,
    sender_ids,
    created_at,
  };

  if (isInboxPage.value && application_ids?.length) {
    query.application_ids = application_ids;
  }

  if (shouldStartFromFirstPage) {
    page.value = "1";
    query.page = "1";
    scrollTop();
  }

  if (shouldAddSender && auth?.currentUser?.id && route.name === ROUTE_INBOX) {
    query.sender_ids = [auth.currentUser.id.toString()];
  }

  const oldQuery = route.query;

  const filledQuery = omitBy(
    query,
    (value) => value === "" || isNullish(value)
  );

  await router.push({ query: filledQuery });

  if (!deepEqual(oldQuery, route.query)) {
    return;
  }

  if (!isInboxPage.value) {
    emit("email-tab:open");
  }
  getCommunicationList(query);
};

useInfiniteScroll(infiniteScrollWrapper, loadMore, {
  interval: 2000,
  canLoadMore: () =>
    store.canLoadMore && !logsLoading.value && !!logs.value.length,
});

watch(
  () => route.query,
  (newValue, oldValue) => {
    const hasFiltersChanged = !deepEqual(
      {
        ...newValue,
        page: undefined,
      },
      {
        ...oldValue,
        page: undefined,
      }
    );

    const shouldStartFromFirstPage =
      hasFiltersChanged &&
      PARAMS_TO_RESET_PAGE.some((param) => newValue[param] !== oldValue[param]);

    if (
      oldValue.type !== CommunicationType.email &&
      newValue.type === CommunicationType.email
    ) {
      handleEmailTabLogs(
        shouldStartFromFirstPage,
        newValue.type !== oldValue.type
      );
      return;
    }

    if (
      oldValue.type !== CommunicationType.call &&
      newValue.type === CommunicationType.call
    ) {
      handleCallTabLogs(shouldStartFromFirstPage);
      return;
    }

    if (
      oldValue.type !== CommunicationType.task &&
      newValue.type === CommunicationType.task
    ) {
      handleTaskTabLogs(shouldStartFromFirstPage);
      return;
    }

    // on the deal's page we are fetching data for every new tab - we only need this for activity tab
    const isActivityTab = [ROUTE_DEAL_ACTIVITY, ROUTE_INBOX, ROUTE_DEALS].some(
      (routeName) => route.name === routeName
    );
    if (isActivityTab) {
      getCommunicationList({
        ...newValue,
        page: shouldStartFromFirstPage ? "1" : (page.value as string),
      });
    }
    if (shouldStartFromFirstPage || hasFiltersChanged) {
      scrollTop();
    }
  }
);

watch(hasNewLogs, (updatedValue, oldValue) => {
  if (updatedValue && !oldValue) {
    refreshLogsHandler();
  }
});

onMounted(async () => {
  const query = cloneDeep(route.query);
  if (
    !query.assignee_ids &&
    auth?.currentUser?.id &&
    !isCurrentTabEmail.value &&
    route.name === ROUTE_INBOX
  ) {
    query.assignee_ids = [auth.currentUser.id.toString()];
  }

  if (
    query.type === CommunicationType.email &&
    !query.sender_ids &&
    auth?.currentUser?.id &&
    isCurrentTabEmail.value &&
    route.name === ROUTE_INBOX
  ) {
    query.sender_ids = [auth.currentUser.id.toString()];
  }

  if (!query.type && !query.email_is_automated && route.name === ROUTE_INBOX) {
    query.email_is_automated = "0";
  }

  if (
    (query.type === CommunicationType.call || !query.type) &&
    auth?.currentUser?.id &&
    route.name === ROUTE_INBOX
  ) {
    query.call_session_caller_ids = [auth.currentUser.id.toString()];
  }

  await getCommunicationList(query);
});

onUnmounted(() => {
  store.resetStore(["dealActivityHub", "activityHubPage"]);
});
</script>
